import { Box, Text } from "grommet";
import { useParams } from "react-router-dom";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { connectSocket, SOCKET_SCOPES } from "../slices/socketReducer";
import { useOnWindowResize } from "rooks";
import { divide, multiply } from "mathjs";
import VideoPlayer from "../components/webgl/VideoPlayer";
import { useGetRouletteByTokenQuery } from "../api/rouletteWidgetApi";
import { useGetOverlaySettingsQuery } from "../api/overlaySettingsApi";
import RouletteComponent from "../components/roulette/RouletteComponent";

const overlayConfig = {
    width: 660,
    height: 372,
    vidWidth: 246,
    vidHeight: 138.38
}

export const OverlaysPage = () => {

    const dispatch = useDispatch();
    const params = useParams();
    const ws = useSelector(state => state.ws.clients['overlay']);
    const connected = useSelector(state => state.ws.connected['overlay']);

    const { data: overlaySettings, isSuccess: areOverlaySettingsLoaded, isError: areOverlaySettingsError } = useGetOverlaySettingsQuery(params.token);

    const rouletteRef = useRef();

    const [currentInteraction, setCurrentInteraction] = useState();
    const [interactionPosition, setInteractionPosition] = useState({ top: 0, left: 0 });
    const [interactionSize, setInteractionSize] = useState({ height: 0, width: 0 });
    const [chromaKeyConfig, setChromaKeyConfig] = useState();
    const [interactionsQueue, setInteractionsQueue] = useState([]);
    const [interactionPlaying, setInteractionPlaying] = useState(false);

    const [showRoulette, setShowRoulette] = useState();
    const [rouletteWinMessage, setRouletteWinMessage] = useState();

    const wsCallback = useCallback((messageOutput) => {
        const body = JSON.parse(messageOutput?.body);
        
        if (body.data.type === 'INTERACTION') {
            // setCurrentInteraction(body);
            // setInteractionsQueue(prevValueList => [...prevValueList, body.interaction]);
            setInteractionsQueue(prevValueList => [...prevValueList, { type: body.data.type, value: body.interaction }]);
        } else if (body.data.type === 'ROULETTE_DONATE') {
            // setShowRoulette({ sender: body.data.sender });
            setInteractionsQueue(prevValueList => [...prevValueList, { id: body.id, type: body.data.type, value: body.data.sender }]);
        }
        
    }, [])

    const handleVideoEnd = useCallback(() => {
        setCurrentInteraction(null);
        setInteractionsQueue(prevValueList => [...prevValueList.slice(1)]);
        setInteractionPlaying(false);
    }, [])

    const wsTopics = [
        {
            path: `/user/queue/overlay/${params.token}`,
            callback: wsCallback
        }
    ]

    useEffect(() => {
        if (!ws && !connected) {
            dispatch(connectSocket('overlay', `${process.env.REACT_APP_WEBSOCKET_URL}/overlay`, wsTopics, "", params.token, SOCKET_SCOPES.PUBLIC))
        }
        if (ws && !connected) {
            console.log("CONNECTING...");
            ws.activate();
        }
        return () => ws && connected ? ws.deactivate() : null;
    }, [ws, connected, wsCallback])

    useOnWindowResize(() => {
        if (currentInteraction?.position) {
            setInteractionPosition({ top: multiply(divide(currentInteraction.position.y, overlayConfig.height), window.innerHeight), left: multiply(divide(currentInteraction.position.x, overlayConfig.width), window.innerWidth) });
            setInteractionSize({ height: multiply(divide(currentInteraction.position.height, overlayConfig.height), window.innerHeight), width: multiply(divide(currentInteraction.position.width, overlayConfig.width), window.innerWidth) });
        }
    }, true, true)

    const showInteraction = useCallback(() => {
        const interaction = interactionsQueue[0];

        setInteractionPlaying(true);

        if (interaction.type === 'INTERACTION') {
            setCurrentInteraction(interaction.value);
            if (interaction.value.chromaKey) {
                setChromaKeyConfig({
                    chromaKey: interaction.value.chromaKey,
                    similarity: interaction.value.similarity,
                    smoothness: interaction.value.smoothness,
                    spill: interaction.value.spill
                })
            } else {
                setChromaKeyConfig(null);
            }
        } else if (interaction.type === 'ROULETTE_DONATE') {
            setShowRoulette({ id: interaction.id, sender: interaction.value });
        }
    }, [interactionsQueue])

    useEffect(() => {
        if (!interactionPlaying && interactionsQueue.length > 0) {
            const timer = setTimeout(showInteraction, 2000);
            return () => clearTimeout(timer);
        }
    }, [interactionPlaying, interactionsQueue, showInteraction])

    useEffect(() => {
        if (showRoulette) {
            const timer = setTimeout(() => {
                rouletteRef.current.spin();
            }, 1000);
            return () => clearTimeout(timer);
        }
    }, [showRoulette])

    const handleWin = useCallback((item) => {
        if (showRoulette) {
            setRouletteWinMessage(`${showRoulette.sender} виграє ${item.name}!`);
            ws.publish({ destination: '/app/overlay/roulette', body: JSON.stringify({ id: showRoulette.id, name: item.name }) });
            const timer = setTimeout(() => {
                setShowRoulette(undefined);
                setRouletteWinMessage(undefined);
                setInteractionsQueue(prevValueList => [...prevValueList.slice(1)]);
                setInteractionPlaying(false);
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [showRoulette])

    useEffect(() => {
        if (currentInteraction?.position) {
            setInteractionPosition({ top: multiply(divide(currentInteraction.position.y, overlayConfig.height), window.innerHeight), left: multiply(divide(currentInteraction.position.x, overlayConfig.width), window.innerWidth) });
            setInteractionSize({ height: multiply(divide(currentInteraction.position.height, overlayConfig.height), window.innerHeight), width: multiply(divide(currentInteraction.position.width, overlayConfig.width), window.innerWidth) });
        }
    }, [currentInteraction])

    if (areOverlaySettingsLoaded) {
        return (
            <Box style={{ position: 'relative', minHeight: '100vh', overflow: 'hidden' }} >
                {currentInteraction && (currentInteraction.isSpot || (!currentInteraction.isFullscreen && !currentInteraction.isSpot && currentInteraction.position)) 
                && <Box style={{ position: 'absolute', top: `${interactionPosition.top}px`, left: `${interactionPosition.left}px`, height: `${interactionSize.height}px`, width: `${interactionSize.width}px` }}>
                    <VideoPlayer 
                        src={currentInteraction.interactionUrl} 
                        removeBgConfig={chromaKeyConfig}
                        autoPlay
                        volume={overlaySettings.volume}
                        persist={false}
                        onEnded={handleVideoEnd}
                    />
                </Box>}
                {currentInteraction && (currentInteraction.isFullscreen || (!currentInteraction.isFullscreen && !currentInteraction.isSpot && !currentInteraction.position)) && <Box>
                    <VideoPlayer 
                        src={currentInteraction.interactionUrl} 
                        removeBgConfig={chromaKeyConfig}
                        autoPlay
                        volume={overlaySettings.volume}
                        persist={false}
                        onEnded={handleVideoEnd}
                    />
                </Box>}
                <Box style={{ opacity: showRoulette ? 1 : 0, transition: "opacity 1s ease" }}><RouletteSection ref={rouletteRef} token={params.token} onWin={handleWin} volume={overlaySettings.volume} /></Box>
                {rouletteWinMessage && <Box align="center"><Text weight={'bold'} size="large" color={'#ffffff'} style={{ textShadow: '2px 0 #000000ff,-2px 0 #000000ff,0 2px #000000ff,0 -2px #000000ff,1px 1px #000000ff,-1px -1px #000000ff,1px -1px #000000ff,-1px 1px #000000ff,0 0 5px #000000ff', paintOrder: 'stroke' }}>{rouletteWinMessage}</Text></Box>}
            </Box>
        )
    }
}

const RouletteSection = forwardRef(({ token, volume, onWin }, ref) => {

    const { data: rouletteData, isLoading: isRouletteDataLoading, isSuccess: isRouletteDataLoaded } = useGetRouletteByTokenQuery(token);

    const rouletteRef = useRef();

    useImperativeHandle(ref, () => ({
        spin: () => rouletteRef.current.spin()
    }), []);

    return (
        <RouletteComponent
            ref={rouletteRef}
            rouletteData={rouletteData}
            spinDuration={10}
            soundSrc={`${process.env.REACT_APP_CDN_BASE_URL}/sounds/tick.mp3`}
            volume={volume}
            onWin={onWin}
        />
    )
})
